import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { EventQuery } from '@mmef/shared/api';
import type { SortOrder } from './sortRows';
import { METRES_PER_MILE } from './ukTowns';

/**
 * The browse filters. Genuinely client state — the user's in-progress choices, which only become
 * a request once they deliberately search.
 *
 * <p>There is no "mode". "Whole country" and "near me" were only ever one question — *where?* — and
 * splitting them into radio buttons made the user pick a search strategy before they could
 * describe what they wanted. Now a single `origin` answers it: null means anywhere, a value means
 * within a radius of that place.
 *
 * <p>Coordinates live here but are never rendered as inputs: an origin is *resolved* — from the
 * browser's geolocation or a town the visitor picked — and shown back to them by name.
 *
 * <p>Two values, not one. The draft is what the form shows and edits; `applied` is the query the
 * last search actually ran. That's what lets the page show a bare map until someone searches, and
 * stops every keystroke from firing a request.
 */

/** A resolved point to search around. `source` lets the form show the right option as selected. */
export interface Origin {
  lat: number;
  lng: number;
  label: string;
  source: 'geo' | 'town';
  /**
   * The rough area a shared location landed in — "Heaton Chapel, Stockport".
   *
   * Only ever set for `source: 'geo'`, and only so the reader can see the browser put them
   * somewhere sensible: wifi positioning is often a few miles out, and a distance column measured
   * from the wrong town is wrong in a way the numbers alone can't reveal. Undefined while it is
   * still being looked up, and stays undefined if the lookup fails — a missing label costs
   * nothing, so nothing waits on it.
   */
  place?: string;
}

interface FiltersState {
  /** null = anywhere in the country. */
  origin: Origin | null;
  /** Miles, because that is what the control offers. Converted to metres in toQuery. */
  radiusMiles: number;
  /**
   * Keywords chosen from the results on screen, not typed.
   *
   * <p>Every suggestion is a word that genuinely occurs in the current results, so a chip can
   * never come back empty — unlike a free-text box, which asks people to guess and punishes a
   * wrong guess with nothing.
   */
  keywords: string[];
  /**
   * The age range they want events for — not their own age. Empty means no age filtering.
   *
   * <p>Applied client-side rather than sent to the API: results are split into "matches" and
   * "doesn't say", so the server would have to return both sets anyway.
   */
  ageFrom: string;
  ageTo: string;
  /**
   * Hide anything dearer than this, in pounds as typed. Applied over loaded results rather than
   * sent to the server, so it stays in step with the age filter — the price you're capping is the
   * price for the ages you asked about.
   *
   * Empty means no cap. An event that states no price is never hidden by it: excluding a listing
   * over a number nobody gave us would be inventing a reason.
   */
  maxPrice: string;
  /** Only events priced at zero. Not the same as unpriced, and must never be conflated with it. */
  freeOnly: boolean;
  /**
   * How the loaded results are ordered. A view preference, not a filter — it changes nothing about
   * which events are shown, so it deliberately doesn't take part in {@link toQuery} and never
   * triggers a fetch.
   *
   * <p>null means "not chosen", which is not the same as "soonest". Until someone picks, the page
   * answers the question they actually asked: searched near a place, nearest first; searched the
   * whole country, soonest first. Storing a concrete default here instead would throw that away
   * and make every location search open on the wrong order.
   */
  sort: SortOrder | null;
  /** Not user-editable while GB is the only coverage; kept so adding countries is a UI change. */
  country: string;
  /** null until the first search. The events query stays skipped while it is. */
  applied: EventQuery | null;
}

const initialState: FiltersState = {
  origin: null,
  radiusMiles: 25,
  keywords: [],
  ageFrom: '',
  ageTo: '',
  maxPrice: '',
  freeOnly: false,
  sort: null,
  country: 'GB',
  applied: null,
};

function toQuery({ origin, radiusMiles, country }: FiltersState): EventQuery {
  if (origin && radiusMiles > 0) {
    return { lat: origin.lat, lng: origin.lng, radiusMeters: Math.round(radiusMiles * METRES_PER_MILE) };
  }
  return { country: country || 'GB' };
}

const filtersSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    /** Geolocation succeeded, or a town was chosen. */
    originResolved(state, action: PayloadAction<Origin>) {
      state.origin = action.payload;
    },
    /**
     * Names the area a shared location landed in, once the lookup comes back.
     *
     * <p>Kept separate from {@link originResolved} because it arrives later and must not disturb
     * anything: the search has already run on the coordinates. Ignored if the origin has changed
     * underneath it — a slow lookup for a location the reader has since replaced with a town would
     * otherwise label the town with the wrong place.
     */
    originPlaceResolved(state, action: PayloadAction<{ lat: number; lng: number; place: string }>) {
      if (!state.origin || state.origin.source !== 'geo') return;
      if (state.origin.lat !== action.payload.lat || state.origin.lng !== action.payload.lng) return;
      state.origin.place = action.payload.place;
    },
    /** Back to searching everywhere. */
    originCleared(state) {
      state.origin = null;
    },
    radiusChanged(state, action: PayloadAction<number>) {
      state.radiusMiles = action.payload;
    },
    keywordToggled(state, action: PayloadAction<string>) {
      const keyword = action.payload;
      state.keywords = state.keywords.includes(keyword)
        ? state.keywords.filter((k) => k !== keyword)
        : [...state.keywords, keyword];
    },
    /**
     * Dropped whenever a new search runs: the suggestions come from the results, so a keyword
     * from the last search may not exist in this one — and a selected chip that isn't on screen
     * would silently hide everything.
     */
    keywordsCleared(state) {
      state.keywords = [];
    },
    ageRangeChanged(state, action: PayloadAction<{ from: string; to: string }>) {
      state.ageFrom = action.payload.from;
      state.ageTo = action.payload.to;
    },
    maxPriceChanged(state, action: PayloadAction<string>) {
      state.maxPrice = action.payload;
      // Setting a cap contradicts "free only" — one of them has to give, and the one just touched
      // is the one the person meant.
      if (action.payload) state.freeOnly = false;
    },
    freeOnlyToggled(state, action: PayloadAction<boolean>) {
      state.freeOnly = action.payload;
      if (action.payload) state.maxPrice = '';
    },
    sortChanged(state, action: PayloadAction<SortOrder>) {
      state.sort = action.payload;
    },
    /** Promotes the draft to the live query — the only thing that triggers a fetch. */
    searchSubmitted(state) {
      state.applied = toQuery(state);
      state.keywords = [];
    },
    filtersReset() {
      return initialState;
    },
    /**
     * Back to the overview: nothing searched and nowhere chosen. Age and price choices are kept —
     * someone stepping back to pick a different city hasn't changed who they are. Keywords go, for
     * the reason given on {@link keywordsCleared}: they were suggestions from results now gone.
     */
    searchCleared(state) {
      state.applied = null;
      state.origin = null;
      state.keywords = [];
    },
  },
});

export const {
  originResolved,
  originPlaceResolved,
  originCleared,
  radiusChanged,
  keywordToggled,
  keywordsCleared,
  ageRangeChanged,
  maxPriceChanged,
  freeOnlyToggled,
  sortChanged,
  searchSubmitted,
  filtersReset,
  searchCleared,
} = filtersSlice.actions;

/**
 * Whether an event survives the price filter.
 *
 * <p>An event with no stated price always passes. Hiding it would mean excluding a listing because
 * of a number nobody supplied — and since most of the catalogue is imported and unpriced, a filter
 * that dropped them would empty the page and look broken rather than selective.
 */
export function withinPrice(
  price: { minMinor: number; free: boolean } | null,
  maxPricePounds: string,
  freeOnly: boolean,
): boolean {
  if (freeOnly) return price?.free === true;
  if (!maxPricePounds) return true;

  const cap = Number.parseFloat(maxPricePounds);
  if (!Number.isFinite(cap)) return true;
  if (!price) return true;

  // Compared in pence, not pounds: 0.1 + 0.2 is famously not 0.3 in binary floating point, and a
  // £30 cap that excluded a £30 ticket would be indefensible.
  return price.minMinor <= Math.round(cap * 100);
}

/**
 * Whether an event's bands overlap the range being looked for.
 *
 * <p>Mirrors the server's rule so search and alerts agree: no wanted range or no stated bands
 * means "yes", and overlap is enough — an event for 25–35 is a fair answer to "show me 30–40".
 */
export function overlapsWantedAges(
  ranges: Array<{ minAge: number | null; maxAge: number | null }>,
  wantedMin: number | null,
  wantedMax: number | null,
): boolean {
  if ((wantedMin === null && wantedMax === null) || ranges.length === 0) return true;
  return ranges.some(
    (r) =>
      (wantedMax === null || r.minAge === null || r.minAge <= wantedMax) &&
      (wantedMin === null || r.maxAge === null || r.maxAge >= wantedMin),
  );
}

export default filtersSlice.reducer;

export const selectFilters = (state: { filters: FiltersState }) => state.filters;

/** What a search would send right now — used to preview, not to fetch. */
export const selectDraftQuery = (state: { filters: FiltersState }) => toQuery(state.filters);

/** The query the last search ran, or null if nobody has searched yet. */
export const selectAppliedQuery = (state: { filters: FiltersState }) => state.filters.applied;

/**
 * Where the map should look when there's nothing to fit yet. Null means "no opinion" and the map
 * falls back to a UK-wide view.
 */
export function selectFocusPoint(state: { filters: FiltersState }): [number, number] | null {
  const { origin } = state.filters;
  return origin ? [origin.lat, origin.lng] : null;
}
